// Social Media Hub Academy — course content, Part C (Sections 15–21)
import type { TrainingSection } from './trainingTypes';

export const SECTIONS_C: TrainingSection[] = [
  // 15 ──────────────────────────────────────────────────────────────────────
  {
    id: 'collaboration',
    num: 15,
    title: 'Team Collaboration',
    short: 'Collaboration',
    icon: 'users',
    minutes: 5,
    intro:
      'The Hub is built for teams: clear roles, revision notes as comments, approvals as assignments, and realm isolation as permission boundaries.',
    objectives: [
      'Understand roles and permission boundaries',
      'Use revision notes for effective feedback',
      'Coordinate creators and approvers without collisions',
    ],
    slides: [
      {
        id: 'collab-roles',
        title: 'Roles, permissions and handoffs',
        blocks: [
          {
            type: 'hotspots',
            title: 'How the team fits together',
            items: [
              { label: 'Creators', detail: 'Run Steps 1–3: voice, weekly generation, media and copy polish. Their output lands in Internal Review.' },
              { label: 'Reviewers', detail: 'Run the five-point checklist and either escalate to Admin Approval or send back revision notes.' },
              { label: 'Administrators', detail: 'Final sign-off, access-code management in the Admin Suite, and export/publish rights.' },
              { label: 'Realm isolation', detail: 'Albania and Romania admins operate in fully separate data realms — a natural permission wall per brand.' },
              { label: 'Temporary access codes', detail: 'The Admin Suite can issue time-limited codes for contractors or seasonal staff — access that expires by itself.' },
              { label: 'Revision notes', detail: 'Notes attached to a state change are the Hub’s comment system — feedback lives ON the post, not in a chat app.' },
            ],
          },
          {
            type: 'callout',
            tone: 'tip',
            title: 'One week, one owner',
            text: 'Assign each date-range week a single owner who drives it from generation to approval. Shared ownership is how posts stall in review.',
          },
          {
            type: 'check',
            question: 'Where should feedback about a specific post live?',
            choices: ['In a revision note attached to the post’s state change', 'In a group chat', 'In an email thread', 'On a sticky note'],
            correctIndex: 0,
            explain: 'Notes on the state change keep feedback attached to the work itself, visible to anyone who opens the post — with full history.',
          },
        ],
        notes:
          'For agencies pitching multi-brand management: realm isolation plus temporary access codes means contractors get exactly the access they need, and it expires automatically. Say “access that cleans up after itself.”',
        takeaways: [
          'Creators → Reviewers → Administrators: a clean relay',
          'Feedback lives on the post as revision notes',
          'Realms + temp codes = permissions without administration overhead',
        ],
      },
    ],
  },

  // 16 ──────────────────────────────────────────────────────────────────────
  {
    id: 'automation',
    num: 16,
    title: 'Automation Features',
    short: 'Automation',
    icon: 'zap',
    minutes: 6,
    intro:
      'Automation removes the repetitive 80% so humans can spend taste on the 20% that matters. Here is every automation in the Hub.',
    objectives: [
      'Name every automation available in the Hub',
      'Know what each automation does — and where humans stay in charge',
    ],
    slides: [
      {
        id: 'auto-list',
        title: 'Every automation, catalogued',
        blocks: [
          {
            type: 'hotspots',
            title: 'The automation catalogue',
            items: [
              { label: 'Brand voice analysis', detail: 'One-click study of your brand that produces the reusable voice profile powering all copy.' },
              { label: 'Weekly generation', detail: 'One action creates 7 posts across 7 distinct content templates with captions, prompts and schedules.' },
              { label: 'Template dealing', detail: 'The engine shuffles the 25-template library and deals a unique mix each week — variety is automatic.' },
              { label: 'Prompt assembly', detail: 'Shot + template style + brand palette are combined into image prompts without human prompt-writing.' },
              { label: 'Auto-scheduling', detail: 'Distributes posts across proven engagement windows; every placement remains editable.' },
              { label: 'Bulk captioning', detail: 'Batch-generates on-voice captions from a topic list.' },
              { label: 'Single-post regeneration', detail: 'Recovers a post’s template and rewrites or re-shoots within it — surgical fixes.' },
              { label: 'Week naming', detail: 'Weeks are automatically labeled by their post date range in every menu.' },
            ],
          },
          {
            type: 'callout',
            tone: 'gold',
            title: 'The human stays the editor-in-chief',
            text: 'Automation drafts; humans decide. Nothing publishes without an explicit admin approval — that gate is deliberate and permanent.',
          },
          {
            type: 'check',
            question: 'Which decision is intentionally NEVER automated?',
            choices: ['Final approval to publish', 'Caption drafting', 'Image prompt assembly', 'Week naming'],
            correctIndex: 0,
            explain: 'The approval gate is a human decision by design — automation prepares, people approve.',
          },
        ],
        notes:
          'Count the automations out loud — eight of them. Then land the governance point: the ONE thing that is never automated is the decision to go live. Enterprise buyers need to hear that explicitly.',
        takeaways: [
          'Eight automations cover drafting, media, scheduling and naming',
          'Approval is the deliberate human gate',
          'Automation buys back hours; taste remains your job',
        ],
      },
    ],
  },

  // 17 ──────────────────────────────────────────────────────────────────────
  {
    id: 'settings',
    num: 17,
    title: 'Settings',
    short: 'Settings',
    icon: 'settings',
    minutes: 4,
    intro:
      'Every configurable option in one tour: credentials, realms, remembered logins, brand inputs, and connection management.',
    objectives: [
      'Configure login, realm and remembered credentials',
      'Manage Instagram connection settings',
      'Know which settings affect generation output',
    ],
    slides: [
      {
        id: 'set-all',
        title: 'The configuration tour',
        blocks: [
          {
            type: 'hotspots',
            title: 'Everything you can configure',
            items: [
              { label: 'Admin credentials & realms', detail: 'Separate logins for /albaniaadmin (Albania) and /romaniaadmin (Romania); each unlocks only its own realm.' },
              { label: 'Remember me', detail: 'Per-realm remembered credentials for fast re-entry on trusted devices.' },
              { label: 'Brand voice inputs', detail: 'The brand description and materials fed to Step 1 — the single most output-affecting setting in the system.' },
              { label: 'Instagram connection', detail: 'Link or relink the destination account in the Instagram Connect panel of Step 5.' },
              { label: 'Temporary access codes', detail: 'Issue and revoke time-limited access from the Administration Suite.' },
              { label: 'Week management', detail: 'Select, lock or delete date-range weeks — deletion removes the week’s posts too.' },
            ],
          },
          {
            type: 'callout',
            tone: 'warn',
            title: 'Handle with care',
            text: 'Brand voice inputs and week deletion are the two settings with the largest blast radius. Change the first deliberately; confirm the second twice.',
          },
          {
            type: 'check',
            question: 'Which setting most directly shapes the quality of every generated caption?',
            choices: ['Brand voice inputs', 'Remember me', 'The week selector', 'Logout'],
            correctIndex: 0,
            explain: 'The voice profile built from your brand inputs is read by every caption generation — garbage in, garbage out.',
          },
        ],
        notes:
          'Short section — settings should be boring. Spend your time on the two dangerous ones: voice inputs (quality blast radius) and week deletion (data blast radius, hence tap-to-confirm).',
        takeaways: [
          'Realm logins keep brands isolated',
          'Voice inputs are the highest-impact setting in the Hub',
          'Week deletion cascades to its posts — confirm intentionally',
        ],
      },
    ],
  },

  // 18 ──────────────────────────────────────────────────────────────────────
  {
    id: 'best-practices',
    num: 18,
    title: 'Tips & Best Practices',
    short: 'Best Practices',
    icon: 'star',
    minutes: 6,
    intro:
      'The recommended weekly operating rhythm, distilled from how the best-performing teams run the Hub.',
    objectives: [
      'Adopt the recommended weekly workflow',
      'Avoid the five most common operator mistakes',
    ],
    slides: [
      {
        id: 'bp-rhythm',
        title: 'The winning weekly rhythm',
        blocks: [
          {
            type: 'steps',
            title: 'A proven operating cadence',
            items: [
              { title: 'Monday — Generate', desc: 'Create next week’s posts while this week runs. Always work one week ahead.' },
              { title: 'Tuesday — Media day', desc: 'Generate/recreate images, upload real photography, polish prompts.' },
              { title: 'Wednesday — Review', desc: 'Run the five-point checklist on all 7 posts in one sitting.' },
              { title: 'Thursday — Approve & export', desc: 'Admin sign-off and Ready-to-Export packaging.' },
              { title: 'Friday — Analyze', desc: 'Review this week’s saves and template performance; adjust next week’s mix.' },
            ],
          },
          {
            type: 'dodont',
            dos: [
              'Work one full week ahead of the calendar',
              'Batch similar tasks (all media, then all reviews)',
              'Let analytics pick 2 of next week’s 7 templates',
              'Keep 30% evergreen even in campaign months',
            ],
            donts: [
              'Generate on the day of publishing — quality dies in a hurry',
              'Leave weeks half-approved over a weekend',
              'Re-run brand voice analysis without a reason',
              'Accumulate abandoned test weeks',
            ],
          },
          {
            type: 'check',
            question: 'What is the golden scheduling habit of high-performing teams?',
            choices: ['Always work one full week ahead', 'Generate posts the morning they publish', 'Approve posts monthly', 'Skip analytics'],
            correctIndex: 0,
            explain: 'Working a week ahead means every post gets media, review and approval time — quality needs a buffer.',
          },
        ],
        notes:
          'Present the Monday-to-Friday cadence as a calendar the client can literally adopt tomorrow. The single most quoted line from this training tends to be: “Always work one week ahead.”',
        takeaways: [
          'Mon generate · Tue media · Wed review · Thu approve · Fri analyze',
          'One week ahead, always',
          'Batching beats context-switching',
        ],
      },
    ],
  },

  // 19 ──────────────────────────────────────────────────────────────────────
  {
    id: 'faq',
    num: 19,
    title: 'Frequently Asked Questions',
    short: 'FAQ',
    icon: 'help',
    minutes: 4,
    intro: 'Quick answers to the questions every new administrator asks in week one.',
    objectives: ['Get instant answers to the most common administrator questions'],
    slides: [
      {
        id: 'faq-main',
        title: 'Administrator FAQ',
        blocks: [
          {
            type: 'faq',
            items: [
              { q: 'Can I edit a post after it has been approved?', a: 'Yes — but edits move it back through review. This is intentional: what publishes is exactly what was approved.' },
              { q: 'What happens when I delete a week?', a: 'The week AND all of its posts are removed. That is why deletion uses a tap-to-confirm button rather than a one-click action.' },
              { q: 'Why do weeks have date-range names like 6/22/26 – 6/28/26?', a: 'The name is computed from the actual dates of the 7 posts inside, so every menu tells you exactly which batch you are looking at.' },
              { q: 'Can two admins work at the same time?', a: 'Yes. Best practice is one owner per week to avoid overlapping edits on the same posts.' },
              { q: 'Do I have to use AI images?', a: 'No — upload real photography any time. Testimonials and team posts should almost always use real photos.' },
              { q: 'Why did my regenerated post keep the same theme?', a: 'By design: regeneration recovers the post’s content template and rewrites within it, so the week’s template mix stays balanced.' },
              { q: 'Does the Romania portal see Albania’s content?', a: 'Never. Realms are fully isolated — separate voice, posts, planner data and logins.' },
              { q: 'What if I fail the certification quiz?', a: 'Retake it as many times as you like. Every attempt draws a fresh set of 10 random questions from the 75-question bank.' },
            ],
          },
        ],
        notes:
          'Run this as rapid-fire Q&A. In live sessions, ask the room for their questions FIRST, answer from this list where possible, and capture new ones for the knowledge base.',
        takeaways: ['Most week-one confusion is answered by approval gating, realm isolation, and date-range week names'],
      },
    ],
  },

  // 20 ──────────────────────────────────────────────────────────────────────
  {
    id: 'troubleshooting',
    num: 20,
    title: 'Troubleshooting',
    short: 'Troubleshooting',
    icon: 'wrench',
    minutes: 5,
    intro: 'The six most common issues, their root causes, and the fastest fixes.',
    objectives: ['Diagnose and resolve the most common operational issues'],
    slides: [
      {
        id: 'ts-main',
        title: 'Issue → cause → fix',
        blocks: [
          {
            type: 'hotspots',
            title: 'Open each issue for the fix',
            items: [
              { label: 'Post missing from export', detail: 'Cause: it is not Admin-Approved. Fix: check the status badge and complete the approval chain.' },
              { label: 'Delete seems to do nothing on mobile', detail: 'Cause: you tapped once — the button arms first. Fix: tap Delete, then tap “Confirm delete” within 4 seconds.' },
              { label: 'Captions feel off-brand', detail: 'Cause: thin or outdated voice inputs. Fix: enrich the brand description in Step 1 and re-analyze deliberately.' },
              { label: 'Images look stylistically wrong', detail: 'Cause: the visual-style portion of the prompt was edited out. Fix: use Recreate to rebuild the prompt from the template.' },
              { label: 'Cannot access the dashboard', detail: 'Cause: session expired or wrong realm login. Fix: log in again at the correct portal (/albaniaadmin vs /romaniaadmin).' },
              { label: 'Week selector shows an unfamiliar week', detail: 'Cause: another admin generated a new batch. Fix: read the date-range labels and select the week you own.' },
            ],
          },
          {
            type: 'callout',
            tone: 'info',
            title: 'The universal first question',
            text: 'When anything seems missing or blocked, check the post’s approval STATE before anything else. State explains 9 of 10 mysteries in the Hub.',
          },
          {
            type: 'check',
            question: 'Delete appears not to work on a phone. What is actually happening?',
            choices: [
              'The button arms on first tap and needs a confirming second tap',
              'Deletion is desktop-only',
              'The post is corrupted',
              'You must clear browser cache first',
            ],
            correctIndex: 0,
            explain: 'The two-tap confirm pattern prevents accidental deletions on touch screens — tap once to arm, tap “Confirm delete” to execute.',
          },
        ],
        notes:
          'Teach the diagnostic habit, not just the fixes: state first, realm second, week label third. That sequence resolves nearly everything without a support ticket.',
        takeaways: [
          'Check approval state first — it explains most “missing” content',
          'Mobile delete = two taps by design',
          'Recreate repairs broken image prompts instantly',
        ],
      },
    ],
  },

  // 21 ──────────────────────────────────────────────────────────────────────
  {
    id: 'summary',
    num: 21,
    title: 'Summary & Certification',
    short: 'Summary',
    icon: 'award',
    minutes: 4,
    intro:
      'The complete journey in one view — then prove your mastery with the certification quiz.',
    objectives: [
      'Review the end-to-end workflow from voice to published post',
      'Prepare for the certification quiz',
    ],
    slides: [
      {
        id: 'sum-journey',
        title: 'The complete workflow, end to end',
        blocks: [
          {
            type: 'workflow',
            title: 'From brand to audience',
            stages: [
              { label: 'Voice', desc: 'Analyze the brand once; reuse forever', tone: 'gold' },
              { label: 'Generate', desc: '7 posts, 7 distinct templates, date-range named week', tone: 'blue' },
              { label: 'Media', desc: 'Template-styled AI images or real photography', tone: 'neutral' },
              { label: 'Approve', desc: 'Checklist review → admin sign-off, full audit trail', tone: 'green' },
              { label: 'Publish', desc: 'Export or post to Instagram on schedule', tone: 'gold' },
              { label: 'Learn', desc: 'Analytics steers next week’s template mix', tone: 'blue' },
            ],
          },
          {
            type: 'bullets',
            title: 'The ideas worth remembering',
            items: [
              'Think in content templates, not posts — identity with variety',
              'Weeks are named by their dates; ownership is per week',
              'Approval is the human gate; automation handles the rest',
              'Saves are the travel metric; analytics is the steering wheel',
              'Work one week ahead, always',
            ],
          },
          {
            type: 'callout',
            tone: 'gold',
            title: 'Ready for certification?',
            text: 'The quiz draws 10 random questions from a 75-question bank covering every section. You need 80% to earn the Social Media Hub Certification. Unlimited retakes, fresh questions every attempt.',
          },
        ],
        notes:
          'Close the loop visually with the six-stage workflow, restate the five big ideas slowly, then hand off to the quiz. For client demos, this is where you ask for the sale: “Imagine this rhythm running for your brand next Monday.”',
        takeaways: [
          'Voice → Generate → Media → Approve → Publish → Learn',
          'You are ready — take the certification quiz',
        ],
      },
    ],
  },
];
